/**
 * User Store Module
 *
 * Manages user information across the application.
 * Loads and stores user data from login and API calls.
 */

const state = {
  currentUser: null,
  isLoggedIn: false,
  // Map of employee IDs to user info for performance plan details
  employeeCache: {},
  // Flag to track if initial user data has been loaded
  initialized: false
};

const getters = {
  getCurrentUser: (state) => state.currentUser,
  isLoggedIn: (state) => state.isLoggedIn,
  getUserId: (state) => state.currentUser?.id || null,
  getUserName: (state) => state.currentUser?.name || '',
  getUserNumber: (state) => state.currentUser?.number || '',
  getUserRole: (state) => state.currentUser?.role || null,
  getUserJobLevel: (state) => state.currentUser?.jobLevel || 0,
  getToken: (state) => state.currentUser?.token || '',
  getEmployeeById: (state) => (employeeId) => state.employeeCache[employeeId] || null,
  getEmployeeJobLevel: (state) => (employeeId) => state.employeeCache[employeeId]?.jobLevel || 0,
  isUserInitialized: (state) => state.initialized
};

const mutations = {
  SET_CURRENT_USER(state, user) {
    state.currentUser = user;
    state.isLoggedIn = !!user;
    state.initialized = true;
  },
  CLEAR_CURRENT_USER(state) {
    state.currentUser = null;
    state.isLoggedIn = false;
    state.initialized = false;
  },
  SET_EMPLOYEE(state, { employeeId, employeeData }) {
    state.employeeCache = {
      ...state.employeeCache,
      [employeeId]: employeeData
    };
  },
  CLEAR_EMPLOYEE_CACHE(state) {
    state.employeeCache = {};
  }
};

const actions = {
  /**
   * Set current user after login
   */
  setCurrentUser({ commit }, user) {
    if (!user) {
      commit('CLEAR_CURRENT_USER');
      return;
    }

    commit('SET_CURRENT_USER', {
      id: user.id,
      name: user.name,
      number: user.number,
      role: user.role,
      jobLevel: user.jobLevel,
      token: user.token,
      phone: user.phone
    });

    // Also store in localStorage for backward compatibility
    if (user.id) localStorage.setItem('userId', user.id);
    if (user.name) localStorage.setItem('userAccount', user.name);
    if (user.number) localStorage.setItem('userNumber', user.number);
    if (user.role) localStorage.setItem('role', user.role);
    if (user.jobLevel) localStorage.setItem('jobLevel', user.jobLevel);
    if (user.token) localStorage.setItem('token', user.token);
    if (user.phone) localStorage.setItem('userPhone', user.phone);
  },

  /**
   * Clear user data on logout
   */
  clearCurrentUser({ commit }) {
    commit('CLEAR_CURRENT_USER');
    commit('CLEAR_EMPLOYEE_CACHE');

    // Clear localStorage
    localStorage.removeItem('userId');
    localStorage.removeItem('userAccount');
    localStorage.removeItem('userNumber');
    localStorage.removeItem('role');
    localStorage.removeItem('jobLevel');
    localStorage.removeItem('token');
    localStorage.removeItem('userPhone');
    sessionStorage.removeItem('token');
  },

  /**
   * Cache employee data for performance plan details
   */
  cacheEmployee({ commit }, { employeeId, employeeData }) {
    commit('SET_EMPLOYEE', { employeeId, employeeData });
  },

  /**
   * Initialize user from localStorage (for page refresh)
   */
  initializeUser({ commit, state }) {
    if (state.initialized && state.currentUser) {
      return;
    }

    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userAccount');
    const userNumber = localStorage.getItem('userNumber');
    const role = localStorage.getItem('role');
    const jobLevel = localStorage.getItem('jobLevel');
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const phone = localStorage.getItem('userPhone');

    if (userId || token) {
      commit('SET_CURRENT_USER', {
        id: userId ? Number(userId) : null,
        name: userName || '',
        number: userNumber || '',
        role: role || null,
        jobLevel: jobLevel ? Number(jobLevel) : 0,
        token: token || '',
        phone: phone || ''
      });
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};